import { useContext, useState } from 'react';
import { Avatar, Divider, Menu, MenuItem } from '@mui/material';
import {
    BookOpen,
    ChevronDown,
    Globe,
    HelpCircle,
    LogOut,
    Settings,
    ShieldCheck,
    User,
    Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../sporttia/all';
import constants from '../../../config/constants';
import { getPath } from '../../../pages/Pages';
import { PERMISSIONS } from '../../../lib/permissions';
import { usePermission } from '../../../lib/usePermission';
import { useLogout } from '../../../lib/useLogout';
import { useSidebarState } from './SidebarStateContext';

type UserMenuItem = {
    caption: string;
    icon: React.ReactElement;
    onClick?: () => void;
    /**
     * Destino fuera del router del gestor. Los ítems que lo traen se pintan
     * como enlace de verdad (igual que los accesos rápidos): se pueden abrir
     * con ⌘/Ctrl+clic, con el botón central o desde el menú contextual.
     */
    href?: string;
    /** Abre `href` en otra pestaña (sólo para destinos externos). */
    newTab?: boolean;
    show?: boolean;
    /** Separador por encima del ítem (sólo antes de cerrar sesión). */
    divider?: boolean;
};

/**
 * Bloque de usuario al pie del sidebar y su menú de cuenta, con la misma
 * composición que sporttia-competitions: avatar cuadrado con la inicial, nombre
 * del operador, email debajo, y el desplegable con el ancho del bloque, iconos
 * lucide y un único separador antes de cerrar sesión.
 */
export default function SidebarUser(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { closeMobile } = useSidebarState();
    const { can, rolesEnabled } = usePermission();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const logout = useLogout();

    const isAdministrator = !!(cxt?.privileges && cxt.privileges.includes(11));

    // Con los roles v8 encendidos mandan los permisos; con ellos apagados, el
    // privilegio 11 de administrador de siempre.
    const canSeeConfiguration = rolesEnabled
        ? can(PERMISSIONS.CONFIGURATION.VIEW)
        : isAdministrator;

    const items: UserMenuItem[] = [
        {
            show: canSeeConfiguration,
            caption: t('configuration.title'),
            icon: <Settings className="h-4 w-4" />,
            onClick: () => navigate(getPath('scConfiguration')),
        },
        // Con los roles encendidos los administradores se gestionan desde
        // "Operadores y roles", así que esta entrada sólo existe sin ellos.
        {
            show: !rolesEnabled && isAdministrator,
            caption: t('configuration.administrators'),
            icon: <Users className="h-4 w-4" />,
            onClick: () => navigate(`${getPath('scRoles')}?tab=administrators`),
        },
        // Pantalla de sporttia-manager: el ingress la sirve en el mismo
        // dominio, así que va como enlace normal (navegación real) y no por el
        // router del gestor, que no la conoce.
        {
            show: rolesEnabled && can(PERMISSIONS.ADMINISTRATION.VIEW),
            caption: t('configuration.operatorsAndRoles'),
            icon: <ShieldCheck className="h-4 w-4" />,
            href: '/operators',
        },
        {
            caption: t('common.myAccount'),
            icon: <User className="h-4 w-4" />,
            onClick: () => navigate(getPath('account')),
        },
        // Con los roles encendidos el Tablón es comunicación con los clientes.
        {
            show: !cxt?.zero && can(PERMISSIONS.COMMUNICATION.VIEW),
            caption: t('scProfile.wall'),
            icon: <Globe className="h-4 w-4" />,
            onClick: () => navigate(getPath('wall')),
        },
        // El manual del gestor, publicado como documentación online. Va antes
        // de la ayuda porque es autoservicio: primero el manual, y si no
        // resuelve, los canales de contacto.
        {
            caption: t('common.documentation'),
            icon: <BookOpen className="h-4 w-4" />,
            href: constants.docs.link,
            newTab: true,
        },
        {
            caption: t('common.help'),
            icon: <HelpCircle className="h-4 w-4" />,
            onClick: () => navigate(getPath('help')),
        },
        {
            divider: true,
            caption: t('sidebar.logout'),
            icon: <LogOut className="h-4 w-4" />,
            onClick: logout,
        },
    ].filter((item) => item.show !== false);

    // Igual que competitions: nombre del operador y su email debajo (si el
    // backend no lo trae, cae al nombre del centro).
    const userName = cxt?.user?.fullName || cxt?.user?.name || '';
    const userEmail = (cxt?.user as { email?: string } | undefined)?.email;
    const userSubtitle = userEmail || cxt?.sc?.name || '';

    const initial =
        userName.substring(0, 1) || cxt?.sc?.name?.substring(0, 1) || '?';

    return (
        <>
            {/* Mientras el menú está abierto el bloque se marca como activo y
                el avatar invierte sus colores, igual que en competitions
                (`data-[state=open]` + `group/user`). */}
            <button
                type="button"
                id="account"
                data-state={anchorEl ? 'open' : 'closed'}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group/user flex h-12 w-full cursor-pointer items-center gap-2 rounded-md p-2 transition-colors"
            >
                <Avatar
                    className="bg-sidebar-primary text-sidebar-primary-foreground group-hover/user:bg-sidebar group-hover/user:text-sidebar-foreground group-data-[state=open]/user:bg-sidebar group-data-[state=open]/user:text-sidebar-foreground h-8 w-8 rounded-lg"
                    variant="rounded"
                    sx={{
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        // La inicial va en peso normal, como en competitions.
                        fontWeight: 400,
                    }}
                >
                    {initial}
                </Avatar>
                {/* `text-sm` va en el envoltorio y no en el nombre, como en
                    competitions: así `leading-tight` calcula sobre 14px (17,5px
                    de línea). Con el tamaño en el hijo, éste fija su propia
                    línea de 20px y el email baja 1,25px con la fila 2,5px más
                    alta. */}
                <span className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{userName}</span>
                    {/* Al pasar por encima y con el menú abierto el email
                        aclara a `sidebar-accent-foreground/80`, como en
                        competitions: si se queda en `/70` sobre el gris del
                        hover se ve bastante más oscuro que el suyo. */}
                    <span className="text-sidebar-foreground/70 group-hover/user:text-sidebar-accent-foreground/80 group-data-[state=open]/user:text-sidebar-accent-foreground/80 truncate text-xs">
                        {userSubtitle}
                    </span>
                </span>
                <ChevronDown className="h-4 w-4" />
            </button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                // Por defecto MUI deja 16px al borde de la ventana y empujaba
                // el menú fuera del eje del bloque de usuario (que está a 8px).
                marginThreshold={8}
                slotProps={{
                    paper: {
                        // Calcado del DropdownMenuContent de competitions:
                        // ancho del disparador (mínimo 14rem), borde, radio de
                        // 8px, 4px de padding y sombra media.
                        sx: {
                            width: anchorEl?.offsetWidth,
                            minWidth: 224,
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'var(--border)',
                            backgroundColor: 'var(--popover)',
                            color: 'var(--popover-foreground)',
                            backgroundImage: 'none',
                            boxShadow:
                                '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            p: 0.5,
                            mt: -0.5,
                        },
                    },
                    list: { sx: { py: 0 } },
                }}
            >
                {items.map((item) => {
                    const menuItem = (
                        <MenuItem
                            key={item.caption}
                            {...(item.href
                                ? {
                                      component: 'a' as const,
                                      href: item.href,
                                      ...(item.newTab
                                          ? {
                                                target: '_blank',
                                                rel: 'noopener noreferrer',
                                            }
                                          : {}),
                                  }
                                : {})}
                            onClick={() => {
                                setAnchorEl(null);
                                closeMobile();
                                item.onClick?.();
                            }}
                            sx={{
                                gap: 1,
                                py: 0.75,
                                px: 1,
                                fontSize: '0.875rem',
                                lineHeight: '20px',
                                minHeight: 0,
                                borderRadius: '4px',
                                // Los ítems que son enlace deben verse igual
                                // que los demás: sin subrayado y con el color
                                // del desplegable, no el de un enlace.
                                color: 'inherit',
                                textDecoration: 'none',
                                // Los iconos van atenuados y el texto normal,
                                // como en el desplegable de competitions. Allí
                                // el icono lleva además `mr-2`, que se suma al
                                // hueco de la fila: el texto arranca a 16px del
                                // icono, no a 8.
                                '& svg': {
                                    color: 'var(--muted-foreground)',
                                    marginRight: 1,
                                },
                            }}
                        >
                            {item.icon}
                            <span>{item.caption}</span>
                        </MenuItem>
                    );

                    return item.divider
                        ? [
                              // Con clases de Tailwind y no con `sx`: MUI da
                              // 8px de margen al separador que sigue a un
                              // `MenuItem` con un selector que gana al `sx`, y
                              // competitions deja 4px y el color del borde.
                              <Divider
                                  key={`${item.caption}-divider`}
                                  className="border-border -mx-1 my-1"
                              />,
                              menuItem,
                          ]
                        : menuItem;
                })}
            </Menu>
        </>
    );
}
